import { ProfileOverview } from "@/components/profile/profile-overview.tsx";
import { SecurityTab } from "@/components/profile/security-tab.tsx";
import { m } from "@/paraglide/messages.js";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { pageTitle } from "@/utils/page-title.ts";

export const Route = createFileRoute("/_authenticated/settings/account")({
	component: AccountPage,
	head: () => ({
		meta: [{ title: pageTitle(m.settings_account()) }],
	}),
});

const SECTION_IDS = ["profile", "security"] as const;

/**
 * The combined Account page: profile and security live on one page; the
 * sidebar's Profile/Security children scroll to these sections via hash.
 * A scroll-spy mirrors the visible section back into the URL hash so the
 * sidebar highlight follows as the user scrolls.
 */
function AccountPage() {
	const navigate = Route.useNavigate();
	// Survives effect re-runs, so the spy never re-fires for the section that
	// is already active (which would loop: navigate → re-render → navigate …).
	const activeSectionRef = useRef<string | null>(null);

	useEffect(() => {
		// Normalize a hash-less entry to #profile so the sidebar child is
		// highlighted from the start and the scroll-spy has a baseline.
		const initialHash = window.location.hash.replace("#", "");
		if (activeSectionRef.current === null) {
			activeSectionRef.current = initialHash || "profile";
			if (initialHash === "") {
				navigate({
					to: "/settings/account",
					hash: "profile",
					replace: true,
					hashScrollIntoView: false,
				});
			}
		}

		const setActive = (id: string) => {
			if (activeSectionRef.current === id) {
				return;
			}
			activeSectionRef.current = id;
			navigate({
				to: "/settings/account",
				hash: id,
				replace: true,
				hashScrollIntoView: false,
			});
		};

		// Track which sections currently cross the viewport's middle band. The
		// lowest one crossing it is the one the user has scrolled into — its id
		// becomes the URL hash (#profile / #security), which lights the matching
		// sidebar item. rootMargin shrinks the viewport to a thin centre line, so
		// only the section actually being read counts as intersecting.
		const visible = new Set<string>();
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						visible.add(entry.target.id);
					} else {
						visible.delete(entry.target.id);
					}
				}
				const intersecting = SECTION_IDS.filter((id) => visible.has(id));
				if (intersecting.length > 0) {
					setActive(intersecting[intersecting.length - 1]);
				}
			},
			{ rootMargin: "-45% 0px -45% 0px", threshold: 0 },
		);
		for (const id of SECTION_IDS) {
			const el = document.getElementById(id);
			if (el) {
				observer.observe(el);
			}
		}
		return () => observer.disconnect();
	}, [navigate]);

	return (
		<div className="space-y-12">
			<section id="profile" className="scroll-mt-6 space-y-6">
				<h2 className="text-xl font-semibold text-foreground">
					{m.settings_personal_data()}
				</h2>
				<ProfileOverview />
			</section>
			<section id="security" className="scroll-mt-6 space-y-6">
				<h2 className="text-xl font-semibold text-foreground">
					{m.profile_tabs_security()}
				</h2>
				<SecurityTab />
			</section>
		</div>
	);
}
